"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

export default function LoginPage() {
  const router = useRouter();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    setLoading(false);
    if (res.ok) {
      router.push("/dashboard");
      router.refresh();
    } else {
      const data = await res.json().catch(() => ({ error: "登录失败" }));
      setError(data.error ?? "登录失败");
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* 左侧品牌区 */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-[#123a9e] via-[#1e5eff] to-[#4f8bff]">
        {/* 装饰性建筑线条 */}
        <svg className="absolute inset-0 w-full h-full opacity-15" preserveAspectRatio="none" viewBox="0 0 800 600">
          {/* 塔吊 */}
          <g stroke="#fff" strokeWidth="3" fill="none">
            <line x1="120" y1="580" x2="120" y2="80" />
            <line x1="60" y1="110" x2="420" y2="90" />
            <line x1="120" y1="80" x2="120" y2="40" />
            <line x1="100" y1="55" x2="140" y2="55" />
            <line x1="380" y1="90" x2="380" y2="200" />
            <line x1="360" y1="200" x2="400" y2="200" />
          </g>
          {/* 楼群轮廓 */}
          <g stroke="#fff" strokeWidth="2.5" fill="none">
            <rect x="480" y="300" width="120" height="280" />
            <rect x="620" y="220" width="150" height="360" />
            <line x1="480" y1="340" x2="600" y2="340" />
            <line x1="480" y1="380" x2="600" y2="380" />
            <line x1="480" y1="420" x2="600" y2="420" />
            <line x1="480" y1="460" x2="600" y2="460" />
            <line x1="480" y1="500" x2="600" y2="500" />
            <line x1="620" y1="260" x2="770" y2="260" />
            <line x1="620" y1="310" x2="770" y2="310" />
            <line x1="620" y1="360" x2="770" y2="360" />
            <line x1="620" y1="410" x2="770" y2="410" />
            <line x1="620" y1="460" x2="770" y2="460" />
            <rect x="250" y="420" width="180" height="160" />
            <line x1="250" y1="455" x2="430" y2="455" />
            <line x1="250" y1="490" x2="430" y2="490" />
            <line x1="250" y1="525" x2="430" y2="525" />
          </g>
        </svg>

        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-3 mb-8">
            <LogoMark size={44} />
            <span className="text-2xl font-bold tracking-wide">CPMS</span>
          </div>
          <h1 className="text-4xl font-bold leading-snug mb-4">
            施工项目
            <br />
            全周期管理系统
          </h1>
          <p className="text-blue-100 text-base leading-relaxed max-w-md">
            从投标商机到竣工结算质保，一个平台管全程。
            阶段流转有据可查，投标、图纸会审、施工组织、签证变更、竣工验收——每一环都不缺件。
          </p>
          <div className="flex gap-8 mt-10 text-sm text-blue-100">
            <div>
              <p className="text-2xl font-bold text-white">6</p>
              <p>业务阶段全覆盖</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">30+</p>
              <p>规划功能模块</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">1</p>
              <p>套项目状态机主线</p>
            </div>
          </div>
        </div>
      </div>

      {/* 右侧登录表单 */}
      <div className="w-full lg:w-[45%] flex items-center justify-center bg-white px-8">
        <div className="w-full max-w-sm">
          {/* 移动端 Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <LogoMark size={32} />
            <span className="font-bold text-lg text-slate-800">CPMS 施工项目管理系统</span>
          </div>

          <h2 className="text-xl font-bold text-slate-800 mb-1">欢迎登录</h2>
          <p className="text-sm text-slate-400 mb-8">请使用您的系统账号登录工作台</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="login-username" className="block text-sm font-medium text-slate-600 mb-1.5">账号</label>
              <input
                id="login-username"
                className="cp-input w-full"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="请输入账号"
              />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-slate-600 mb-1.5">密码</label>
              <input
                id="login-password"
                type="password"
                className="cp-input w-full"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
              />
            </div>
            {error && (
              <p className="text-sm text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
            )}
            <button type="submit" disabled={loading} className="cp-btn cp-btn-primary w-full justify-center !py-2.5">
              {loading ? "登录中..." : "登 录"}
            </button>
          </form>

          <p className="text-xs text-slate-300 text-center mt-8">账号由系统管理员分配 · 如忘记密码请联系管理员</p>
        </div>
      </div>
    </div>
  );
}

function LogoMark({ size = 36 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect width="48" height="48" rx="10" fill="#ffffff" fillOpacity="0.18" />
      <path d="M10 38V22l8-6 8 6v16H10z" stroke="#fff" strokeWidth="3" strokeLinejoin="round" />
      <path d="M26 38V14l12-4v28" stroke="#fff" strokeWidth="3" strokeLinejoin="round" />
      <line x1="26" y1="20" x2="38" y2="17" stroke="#FFB25C" strokeWidth="3" strokeLinecap="round" />
      <circle cx="24" cy="42" r="2" fill="#FFB25C" />
    </svg>
  );
}
